import { AdPositionElements, insertBanners } from '@schibsted-nmp/advertising-companion';
import { Pagination } from '@warp-ds/react/components/pagination';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { SpinnerIcon } from 'shared/icons/SpinnerIcon';
import type { Filters, ResultData } from '@/domain';
import type { LoginId } from '@/types/common';
import { useResultsTracking } from '../hooks/useResultsTracking';
import { parseUrl } from '../utils/parseUrl';
import { EmptyResults } from './EmptyResults';
import { FetchFailedMessage } from './FetchFailedMessage';
import { ResultItem } from './resultItem/ResultItem';

type ResultsListProps = {
    data: ResultData | undefined;
    loading: boolean;
    error: unknown;
    filters: Filters;
    loginId?: LoginId;
    onFilterChanged: (filter: Filters) => void;
};

const PRELOADED_IMAGES_COUNT = 4;

export const ResultsList = ({ data, loading, error, filters, loginId, onFilterChanged }: ResultsListProps) => {
    const router = useRouter();
    const [bannerPositions, bannerElements] = AdPositionElements.use({ layoutType: 'grid' });

    useResultsTracking(loading, error, data);

    const searchParams = useMemo(() => parseUrl(router.query), [router.query]);
    const results = data?.resultItems ?? null;

    const itemsWithBanners = useMemo(
        () =>
            insertBanners({
                adItems:
                    results && Array.isArray(results)
                        ? results.map((ad, index) => (
                              <ResultItem
                                  key={`${ad.id}${ad.upsell ? '-fus' : ''}`}
                                  rank={index}
                                  ad={ad}
                                  filters={filters}
                                  loading={index < PRELOADED_IMAGES_COUNT ? 'eager' : 'lazy'}
                                  loginId={loginId}
                              />
                          ))
                        : [],
                bannerPositions: [...bannerPositions],
                bannerElements: [...bannerElements],
            }),
        [results, filters, loginId, bannerPositions, bannerElements],
    );

    const createHref = (page: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', String(page));
        return `?${params.toString()}`;
    };

    const handlePageChange = (page: number) => {
        onFilterChanged({ page });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading) {
        return (
            <div className="p-64 flex flex-col items-center w-full" data-testid="loadingSpinner">
                <SpinnerIcon size={24} />
            </div>
        );
    }

    if (error || !results) {
        return <FetchFailedMessage />;
    }

    if (results.length === 0) {
        return <EmptyResults />;
    }

    return (
        <section id="ads" data-testid="resultList" className="w-full">
            <h2 className="sr-only">Søkeresultater</h2>
            <div className="grid grid-flow-row-dense md:grid-cols-2 gap-16">{itemsWithBanners}</div>
            <Pagination
                currentPage={data?.stats?.currentPage ?? 1}
                lastPage={data?.stats?.totalPages ?? 1}
                createHref={createHref}
                onChange={handlePageChange}
                className="my-24"
            />
        </section>
    );
};
